import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Inventory,
  TrendingUp,
  Assignment,
  PersonAdd,
  Close,
} from '@mui/icons-material';
import {departmentService} from '../../services/departmentService';
import { useSnackbar } from 'notistack';
import { DepartmentStockView } from './DepartmentStockView';
import { userService } from '../../services/userService';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const DepartmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [department, setDepartment] = useState(null);
  const [itemIssues, setItemIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [updatingManager, setUpdatingManager] = useState(false);

  useEffect(() => {
    fetchDepartmentDetails();
  }, [id]);

  const fetchDepartmentDetails = async () => {
    try {
      const departmentResponse = await departmentService.getDepartment(id);
      // Backend returns data in response.data.data format
      const responseData = departmentResponse.data?.data || departmentResponse.data;
      const departmentData = responseData?.department || responseData;
      
      // Add stockSummary to department object
      if (responseData?.stockSummary) {
        departmentData.stockSummary = responseData.stockSummary;
      }
      
      setDepartment(departmentData);
      
      // Get recent issues from the same response
      const recentIssues = responseData?.recentIssues || [];
      setItemIssues(recentIssues);
      
      // Fetch consumption data if available
      try {
        const consumptionResponse = await departmentService.getDepartmentConsumption(id);
        const consumptionData = consumptionResponse.data?.data || consumptionResponse.data || [];
        // Only update if we got more data
        if (Array.isArray(consumptionData) && consumptionData.length > 0) {
          setItemIssues(consumptionData);
        }
      } catch (error) {
        // Consumption endpoint might not be available yet, use recentIssues
        console.log('Consumption data not available, using recent issues');
      }
    } catch (error) {
      enqueueSnackbar('Error fetching department details', { variant: 'error' });
      console.error('Error fetching department details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenManagerDialog = async () => {
    setManagerDialogOpen(true);
    setSelectedManager(department.manager?._id || '');
    
    // Fetch users for manager selection
    try {
      setLoadingUsers(true);
      const response = await userService.getManagerUsers({ limit: 100 });
      const usersData = response.data?.data?.users || response.data?.users || [];
      setUsers(usersData);
    } catch (error) {
      enqueueSnackbar('Error fetching users', { variant: 'error' });
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCloseManagerDialog = () => {
    setManagerDialogOpen(false);
    setSelectedManager('');
  };

  const handleAssignManager = async () => {
    try {
      setUpdatingManager(true);
      await departmentService.updateDepartment(id, {
        ...department,
        manager: selectedManager || undefined
      });
      enqueueSnackbar('Manager assigned successfully!', { variant: 'success' });
      handleCloseManagerDialog();
      fetchDepartmentDetails(); // Refresh data
    } catch (error) {
      enqueueSnackbar('Error assigning manager', { variant: 'error' });
      console.error('Error assigning manager:', error);
    } finally {
      setUpdatingManager(false);
    }
  };

  const getInitials = (departmentName) => {
    const words = departmentName?.split(' ') || [];
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
    }
    return departmentName?.substring(0, 2).toUpperCase() || "";
  };

  const getDepartmentTypeColor = (type) => {
    const colors = {
      production: 'primary',
      warehouse: 'secondary',
      maintenance: 'warning',
      administration: 'info',
      research: 'success',
      other: 'default'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!department) {
    return (
      <Alert severity="error">
        Department not found
      </Alert>
    );
  }

  // Debug log
  console.log('DepartmentDetails rendering with department:', department);
  console.log('Manager data:', department.manager);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/hospital-inventory/department')}
          variant="outlined"
        >
          Back to Departments
        </Button>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Department Details
        </Typography>
        <Button
          startIcon={<Edit />}
          variant="contained"
          onClick={() => navigate(`/hospital/hospital-inventory/departments/${id}/edit`)}
        >
          Edit Department
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Department Information */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  mx: 'auto',
                  mb: 2,
                  fontSize: '2rem',
                  fontWeight: 'bold',
                }}
              >
                {getInitials(department.departmentName)}
              </Avatar>
              
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {department.departmentName}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {department.departmentCode}
              </Typography>
              
              <Chip
                label={department.departmentType}
                color={getDepartmentTypeColor(department.departmentType)}
                sx={{ mb: 1 }}
              />
              
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={department.isActive ? 'Active' : 'Inactive'}
                  color={department.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Department ID: {department.departmentId}
              </Typography>

              <Box sx={{ textAlign: 'left', mt: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Contact Information
                </Typography>
                {department.contactPerson && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Contact Person:</strong> {department.contactPerson}
                  </Typography>
                )}
                <Typography variant="body2" gutterBottom>
                  <strong>Phone:</strong> {department.contactPhone}
                </Typography>
                {department.contactEmail && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Email:</strong> {department.contactEmail}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Location Information */}
          {department.location && (department.location.building || department.location.floor || department.location.room) && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Location
                </Typography>
                {department.location.building && (
                  <Typography variant="body2">
                    <strong>Building:</strong> {department.location.building}
                  </Typography>
                )}
                {department.location.floor && (
                  <Typography variant="body2">
                    <strong>Floor:</strong> {department.location.floor}
                  </Typography>
                )}
                {department.location.room && (
                  <Typography variant="body2">
                    <strong>Room:</strong> {department.location.room}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Manager Information - UPDATED VERSION */}
          <Card sx={{ mt: 3 }} data-testid="manager-card">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Manager
                </Typography>
                <Button
                  size="small"
                  startIcon={<PersonAdd />}
                  onClick={handleOpenManagerDialog}
                  variant="outlined"
                  color="primary"
                >
                  {department.manager ? 'Change' : 'Assign'}
                </Button>
              </Box>
              {department.manager ? (
                <>
                  <Typography variant="body2">
                    <strong>Name:</strong> {department.manager.name || 'N/A'}
                  </Typography>
                  {department.manager.email && (
                    <Typography variant="body2">
                      <strong>Email:</strong> {department.manager.email}
                    </Typography>
                  )}
                  {department.manager.phone && (
                    <Typography variant="body2">
                      <strong>Phone:</strong> {department.manager.phone}
                    </Typography>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No manager assigned yet. Click "Assign" to assign a manager to this department.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Budget Information */}
          {department.budgetCode && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Budget Information
                </Typography>
                <Typography variant="body2">
                  <strong>Budget Code:</strong> {department.budgetCode}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Department Stock & Activity */}
        <Grid item xs={12} md={8}>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Stock Items
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {department.stockSummary?.totalItems || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Quantity
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {department.stockSummary?.totalQuantity || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Low Stock Items
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {department.stockSummary?.lowStockItems || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Department Stock" icon={<Inventory />} iconPosition="start" />
                <Tab label="Item Issues" icon={<Assignment />} iconPosition="start" />
                <Tab label="Consumption" icon={<TrendingUp />} iconPosition="start" />
              </Tabs>

              {/* Department Stock Tab */}
              <TabPanel value={tabValue} index={0}>
                <DepartmentStockView departmentId={id} />
              </TabPanel>

              {/* Item Issues Tab */}
              <TabPanel value={tabValue} index={1}>
                {itemIssues.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Issue #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Purpose</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemIssues.map((issue) => (
                          <TableRow key={issue._id}>
                            <TableCell>
                              <Typography variant="subtitle2">
                                {issue.issueNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {new Date(issue.issueDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={issue.purpose}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" fontWeight="bold">
                                ₹{issue.totalCost?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={issue.status}
                                size="small"
                                color={
                                  issue.status === 'issued' ? 'success' :
                                  issue.status === 'approved' ? 'info' :
                                  issue.status === 'pending_approval' ? 'warning' : 'default'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No item issues found for this department
                  </Typography>
                )}
              </TabPanel>

              {/* Consumption Tab */}
              <TabPanel value={tabValue} index={2}>
                <Alert severity="info">
                  Consumption analytics will be available once item issues are recorded for this department.
                </Alert>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Consumption trends and analytics coming soon
                  </Typography>
                </Box>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manager Assignment Dialog */}
      <Dialog open={managerDialogOpen} onClose={handleCloseManagerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Assign Manager</Typography>
            <IconButton onClick={handleCloseManagerDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a user to assign as manager for this department. Only users with manager or admin roles are shown.
              </Typography>
              <TextField
                select
                fullWidth
                label="Select Manager"
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                helperText="Leave empty to remove current manager"
              >
                <MenuItem value="">
                  <em>None (Remove Manager)</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    <Box>
                      <Typography variant="body2">{user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email} • {user.role}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManagerDialog}>Cancel</Button>
          <Button
            onClick={handleAssignManager}
            variant="contained"
            disabled={updatingManager || loadingUsers}
          >
            {updatingManager ? 'Assigning...' : 'Assign Manager'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
